import { useState, useEffect, useCallback } from "react";
import { serializationService } from "../services/serialization/serializationService";
import { useAuth } from "../context/AuthContext";

/**
 * Hook for using serialization in React components
 * @param {string} entityType - Type of entity to serialize
 * @param {Object} entity - Entity to serialize
 * @param {string} version - Target version (optional)
 * @returns {Object} Serialization state and methods
 */
export const useSerialization = (entityType, entity, version = null) => {
    const [serializedEntity, setSerializedEntity] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [versionHistory, setVersionHistory] = useState([]);
    const { user } = useAuth();

    // Serialize entity when it changes
    useEffect(() => {
        if (entity && entityType) {
            serializeEntity();
        }
    }, [entity, entityType, version, serializeEntity]);

    // Load version history when entity changes
    useEffect(() => {
        if (entity?.id && entityType) {
            loadVersionHistory();
        }
    }, [entity?.id, entityType, loadVersionHistory]);

    /**
     * Serialize the entity
     */
    const serializeEntity = useCallback(async () => {
        if (!entity || !entityType) return;

        try {
            setLoading(true);
            setError(null);

            const context = {
                userId: user?.uid || "anonymous",
                ipAddress: "client-side",
                userAgent: navigator.userAgent,
                isAdminAction: user?.role === "admin"
            };

            const serialized = await serializationService.serialize(
                entity, 
                entityType, 
                version, 
                context
            );

            setSerializedEntity(serialized);

        } catch (err) {
            setError(err.message);
            // eslint-disable-next-line no-console
            console.error("Serialization failed:", err);
        } finally {
            setLoading(false);
        }
    }, [entity, entityType, version, user]);

    /**
     * Deserialize the entity
     */
    const deserializeEntity = useCallback(async () => {
        if (!serializedEntity || !entityType) return null;

        try {
            setLoading(true);
            setError(null);

            const context = {
                userId: user?.uid || "anonymous",
                ipAddress: "client-side",
                userAgent: navigator.userAgent
            };

            const deserialized = await serializationService.deserialize(
                serializedEntity,
                entityType,
                context
            );

            return deserialized;

        } catch (err) {
            setError(err.message);
            // eslint-disable-next-line no-console
            console.error("Deserialization failed:", err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [serializedEntity, entityType, user]);

    /**
     * Load version history for the entity
     */
    const loadVersionHistory = useCallback(async () => {
        if (!entity?.id || !entityType) return;

        try {
            const history = await serializationService.getVersionHistory(entity.id, entityType);
            setVersionHistory(history);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to load version history:", err);
        }
    }, [entity?.id, entityType]);

    /**
     * Check if entity needs migration
     */
    const needsMigration = useCallback(() => {
        if (!entity || !entityType) return false;
        return serializationService.needsMigration(entity, entityType);
    }, [entity, entityType]);

    /**
     * Get available versions for the entity type
     */
    const getAvailableVersions = useCallback(() => {
        return serializationService.getAvailableVersions(entityType);
    }, [entityType]);

    /**
     * Compare current entity with a specific version
     */
    const compareWithVersion = useCallback((_targetVersion) => {
        if (!serializedEntity || !entityType) return null;

        // This would require fetching the target version from storage
        // For now, return null
        return null;
    }, [serializedEntity, entityType]);

    /**
     * Force re-serialization
     */
    const refresh = useCallback(() => {
        serializeEntity();
    }, [serializeEntity]);

    return {
        // State
        serializedEntity,
        loading,
        error,
        versionHistory,
        
        // Methods
        serializeEntity,
        deserializeEntity,
        needsMigration,
        getAvailableVersions,
        compareWithVersion,
        refresh,
        
        // Computed values
        currentVersion: serializedEntity?.version,
        serializedAt: serializedEntity?.serializedAt,
        serializedBy: serializedEntity?.serializedBy,
        changeReason: serializedEntity?.changeReason,
        regulatoryRequirement: serializedEntity?.regulatoryRequirement
    };
};

/**
 * Hook for managing multiple entities with serialization
 * @param {string} entityType - Type of entities
 * @param {Array} entities - Array of entities
 * @returns {Object} Serialization state and methods for multiple entities
 */
export const useBatchSerialization = (entityType, entities) => {
    const [serializedEntities, setSerializedEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { user } = useAuth();

    useEffect(() => {
        if (entities && entities.length > 0 && entityType) {
            serializeEntities();
        }
    }, [entities, entityType, serializeEntities]);

    const serializeEntities = useCallback(async () => {
        if (!entities || entities.length === 0 || !entityType) return;

        try {
            setLoading(true);
            setErrors({});

            const context = {
                userId: user?.uid || "anonymous",
                ipAddress: "client-side",
                userAgent: navigator.userAgent,
                isAdminAction: user?.role === "admin"
            };

            const serializedPromises = entities.map(async (entity) => {
                try {
                    return await serializationService.serialize(entity, entityType, null, context);
                } catch (error) {
                    setErrors(prev => ({
                        ...prev,
                        [entity.id]: error.message
                    }));
                    return null;
                }
            });

            const results = await Promise.all(serializedPromises);
            const validResults = results.filter(result => result !== null);
            
            setSerializedEntities(validResults);

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Batch serialization failed:", error);
        } finally {
            setLoading(false);
        }
    }, [entities, entityType, user]);

    const refresh = useCallback(() => {
        serializeEntities();
    }, [serializeEntities]);

    return {
        serializedEntities,
        loading,
        errors,
        refresh
    };
}; 